import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useCreateSource, useUpdateSource } from "../../hooks/useApi";
import { useTranslation } from "../../hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import ValidationMessage from "../forms/ValidationMessage";
import { validationRules, parseApiError } from "../../utils/validation";
import { FileText, Eye, Edit3, Info } from "lucide-react";

const TextSourceDialog = ({
  isOpen,
  onOpenChange,
  botId,
  onSuccess,
  editSource = null,
}) => {
  const createSourceMutation = useCreateSource();
  const updateSourceMutation = useUpdateSource();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("edit");

  const isEditMode = Boolean(editSource);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      content: "",
    },
    mode: "onChange",
  });

  // Prefill the form when opening in edit mode (and clear it in create mode)
  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      reset({
        title: editSource.title || "",
        content: editSource.inline_text || "",
      });
    } else {
      reset({ title: "", content: "" });
    }
  }, [isOpen, isEditMode, editSource, reset]);

  const watchedContent = watch("content");
  const watchedTitle = watch("title");

  // Calculate content statistics
  const contentStats = {
    characters: watchedContent?.length || 0,
    words: watchedContent?.trim()
      ? watchedContent.trim().split(/\s+/).length
      : 0,
    lines: watchedContent?.split("\n").length || 0,
    paragraphs: watchedContent?.trim()
      ? watchedContent.trim().split(/\n\s*\n/).length
      : 0,
  };

  // Estimate reading time (average 200 words per minute)
  const estimatedReadingTime = Math.ceil(contentStats.words / 200);

  // Handle form submission (create or update depending on the mode)
  const onSubmit = async (data) => {
    try {
      clearErrors();

      if (isEditMode) {
        await updateSourceMutation.mutateAsync({
          botId,
          sourceId: editSource._id,
          data: {
            title: data.title?.trim() || "Text Document",
            inline_text: data.content,
          },
        });
      } else {
        await createSourceMutation.mutateAsync({
          botId,
          data: {
            type: "text",
            title: data.title?.trim() || "Text Document",
            inline_text: data.content,
          },
        });
      }

      // Reset form and close dialog
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error(
        isEditMode
          ? "Failed to update text source:"
          : "Failed to create text source:",
        error
      );

      // Parse API error and set field-specific errors
      const parsedError = parseApiError(error);
      if (parsedError.type === "validation" && parsedError.fieldErrors) {
        Object.entries(parsedError.fieldErrors).forEach(([field, message]) => {
          setError(field, { message });
        });
      }
    }
  };

  // Sample text for demonstration
  const insertSampleText = () => {
    const sampleText = t(
      "dialogs.text.sampleBody",
      `# Sample Knowledge Base Article

## Introduction

This is a sample text document that demonstrates how to add textual content to your bot's knowledge base. You can replace this content with your own information.

## Key Features

- **Rich Text Support**: Add formatted text with headings, lists, and paragraphs
- **Instant Processing**: Text content is processed immediately after creation
- **Searchable Content**: All text becomes searchable through your bot's query interface

## Best Practices

When adding text content to your knowledge base:

1. Use clear, descriptive headings to organize information
2. Break content into logical sections and paragraphs
3. Include relevant keywords that users might search for
4. Keep information accurate and up-to-date

## Conclusion

Text sources are perfect for adding structured information, FAQs, documentation, and other textual knowledge to your bot.`
    );

    setValue("content", sampleText);
    setValue(
      "title",
      t("dialogs.text.sampleTitle", "Sample Knowledge Base Article")
    );
  };

  // Reset form when dialog closes
  const handleOpenChange = (open) => {
    if (!open) {
      reset();
      setActiveTab("edit");
    }
    onOpenChange(open);
  };

  const isSubmitting = isEditMode
    ? updateSourceMutation.isPending
    : createSourceMutation.isPending;
  const submitError = isEditMode
    ? updateSourceMutation.error
    : createSourceMutation.error;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode
              ? t("dialogs.text.titleEdit", "Edit Text Content")
              : t("dialogs.text.titleAdd", "Add Text Content")}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? t(
                  "dialogs.text.descEdit",
                  "Update this text content. Changing the text will re-index it in your bot's knowledge base."
                )
              : t(
                  "dialogs.text.descAdd",
                  "Add text content directly to your bot's knowledge base. Perfect for FAQs, documentation, and structured information."
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title Input */}
          <div className="space-y-2">
            <Label htmlFor="title" className="block text-sm font-medium">
              {t("dialogs.text.titleLabel", "Title")}{" "}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="title"
              placeholder={t(
                "dialogs.text.titlePlaceholder",
                "Enter a title for this text content"
              )}
              disabled={isSubmitting}
              className={
                errors.title
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : ""
              }
              {...register("title", {
                validate: validationRules.sourceTitle,
              })}
            />
            {errors.title && (
              <ValidationMessage type="error" message={errors.title.message} />
            )}
          </div>

          {/* Content Input with Tabs */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>{t("dialogs.text.contentLabel", "Content")}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={insertSampleText}
                disabled={isSubmitting}
              >
                {t("dialogs.text.insertSample", "Insert Sample Text")}
              </Button>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value="edit"
                  className="flex items-center space-x-2"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>{t("dialogs.text.tabEdit", "Edit")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="preview"
                  className="flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>{t("dialogs.text.tabPreview", "Preview")}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="space-y-2">
                <textarea
                  {...register("content", {
                    validate: validationRules.sourceText,
                  })}
                  placeholder={t(
                    "dialogs.text.contentPlaceholder",
                    "Enter your text content here. You can use markdown formatting for better structure."
                  )}
                  className="w-full h-64 p-3 border border-input rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  disabled={isSubmitting}
                />
                {errors.content && (
                  <ValidationMessage
                    type="error"
                    message={errors.content.message}
                  />
                )}
              </TabsContent>

              <TabsContent value="preview" className="space-y-2">
                <div className="w-full h-64 p-3 border border-input rounded-md bg-muted/30 overflow-y-auto">
                  {watchedContent ? (
                    <div className="prose prose-sm max-w-none">
                      {watchedContent.split("\n").map((line, index) => {
                        // Simple markdown-like rendering for preview
                        if (line.startsWith("# ")) {
                          return (
                            <h1 key={index} className="text-xl font-bold mb-2">
                              {line.substring(2)}
                            </h1>
                          );
                        } else if (line.startsWith("## ")) {
                          return (
                            <h2
                              key={index}
                              className="text-lg font-semibold mb-2"
                            >
                              {line.substring(3)}
                            </h2>
                          );
                        } else if (line.startsWith("### ")) {
                          return (
                            <h3
                              key={index}
                              className="text-base font-medium mb-1"
                            >
                              {line.substring(4)}
                            </h3>
                          );
                        } else if (
                          line.startsWith("- ") ||
                          line.startsWith("* ")
                        ) {
                          return (
                            <li key={index} className="ml-4">
                              {line.substring(2)}
                            </li>
                          );
                        } else if (line.match(/^\d+\. /)) {
                          return (
                            <li key={index} className="ml-4 list-decimal">
                              {line.replace(/^\d+\. /, "")}
                            </li>
                          );
                        } else if (line.trim() === "") {
                          return <br key={index} />;
                        } else {
                          return (
                            <p key={index} className="mb-2">
                              {line}
                            </p>
                          );
                        }
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <FileText className="mx-auto h-8 w-8 mb-2" />
                        <p>
                          {t(
                            "dialogs.text.previewEmpty",
                            "Content preview will appear here"
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Content Statistics */}
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-center space-x-1 mb-2">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {t("dialogs.text.statsTitle", "Content Statistics")}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    {t("dialogs.text.statsCharacters", "Characters:")}
                  </span>
                  <span className="ml-1 font-medium">
                    {contentStats.characters.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t("dialogs.text.statsWords", "Words:")}
                  </span>
                  <span className="ml-1 font-medium">
                    {contentStats.words.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t("dialogs.text.statsLines", "Lines:")}
                  </span>
                  <span className="ml-1 font-medium">
                    {contentStats.lines.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t("dialogs.text.statsReading", "Est. Reading:")}
                  </span>
                  <span className="ml-1 font-medium">
                    {estimatedReadingTime} {t("dialogs.text.minutes", "min")}
                  </span>
                </div>
              </div>
            </div>

            {/* Validation Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">
                    {t("dialogs.text.guidelinesTitle", "Content Guidelines")}
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li>
                      {t(
                        "dialogs.text.guidelineMin",
                        "• Minimum 50 characters required"
                      )}
                    </li>
                    <li>
                      {t(
                        "dialogs.text.guidelineMax",
                        "• Maximum 100,000 characters allowed"
                      )}
                    </li>
                    <li>
                      {t(
                        "dialogs.text.guidelineHeadings",
                        "• Use headings (# ## ###) to structure content"
                      )}
                    </li>
                    <li>
                      {t(
                        "dialogs.text.guidelineChunked",
                        "• Content will be automatically chunked for optimal search"
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {submitError && (
            <ValidationMessage
              type="error"
              message={parseApiError(submitError).message}
              showIcon={true}
              size="md"
            />
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isEditMode
                ? isSubmitting
                  ? t("dialogs.text.saving", "Saving...")
                  : t("dialogs.text.saveChanges", "Save Changes")
                : isSubmitting
                ? t("dialogs.text.creating", "Creating...")
                : t("dialogs.text.addSource", "Add Text Source")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TextSourceDialog;
